/**
 * Simulation Utils
 * Pure functions for computing simulation step statuses from lifecycle data
 */

import type {
  OrderLifecycleResponse,
  SimulationStep,
  SimulationStepId,
  SimulationStepStatus,
  ActivityEntry,
} from '../types/simulation.types'

const STEP_LABELS: Record<SimulationStepId, string> = {
  order_created: 'Order Created',
  assign_resource: 'Assign Resource',
  join_queue: 'Join Queue (Kiosk)',
  entry_scan: 'Entry Scan',
  weighbridge_scan: 'Weighbridge Scan',
  first_weigh: 'Entry First Weight (Tare)',
  second_weigh: 'Entry Second Weight (Gross)',
  exit_scan: 'Exit Scan',
  invoices: 'Invoices',
}

function isStepCompleted(stepId: SimulationStepId, lifecycle: OrderLifecycleResponse): boolean {
  switch (stepId) {
    case 'order_created':
      return true

    case 'assign_resource':
      return Boolean(lifecycle.shipment?.hasResource)

    case 'join_queue':
      return Boolean(lifecycle.order.sapSalesOrderNumber)

    case 'entry_scan': {
      const s = lifecycle.shipment
      if (!s) return false
      const enteredOrBeyond = ['entered', 'loading'].includes(lifecycle.queueEntry?.status ?? '')
      const loadingOrBeyond = ['loading', 'arrived', 'completed', 'delivered', 'cancelled'].includes(s.shipmentStatus)
      return enteredOrBeyond || loadingOrBeyond || lifecycle.weighbridgeTickets.length > 0
    }

    case 'weighbridge_scan': {
      // Considered done once weighbridge tickets exist OR shipment is past loading stage
      const s = lifecycle.shipment
      if (!s) return false
      return lifecycle.weighbridgeTickets.length > 0 ||
        ['arrived', 'completed', 'delivered', 'cancelled'].includes(s.shipmentStatus)
    }

    case 'first_weigh': {
      const ticket = lifecycle.weighbridgeTickets[0]
      return Boolean(ticket?.tareWeight && ticket.tareWeight > 0)
    }

    case 'second_weigh': {
      const ticket = lifecycle.weighbridgeTickets[0]
      return Boolean(ticket?.grossWeight && ticket.grossWeight > 0 && ticket?.netWeight)
    }

    case 'exit_scan':
      return lifecycle.shipment?.shipmentStatus === 'completed' ||
        lifecycle.shipment?.shipmentStatus === 'delivered'

    case 'invoices':
      return lifecycle.invoices.length > 0

    default:
      return false
  }
}

function isStepFailed(stepId: SimulationStepId, lifecycle: OrderLifecycleResponse): boolean {
  if (stepId === 'exit_scan') {
    if (lifecycle.shipment?.holdReason) return true
    // SAP rejected the second weight update → job is in DLQ and shipment is still loading
    const hasFailedWeightUpdate = lifecycle.sapSyncJobs.some(
      (j) => j.sapOperation === 'weight_update_second' &&
        (j.jobStatus === 'dlq' || j.jobStatus === 'failed'),
    )
    if (hasFailedWeightUpdate && lifecycle.shipment?.shipmentStatus === 'loading') return true
  }
  if (stepId === 'entry_scan' && lifecycle.order.orderStatus === 'failed') {
    return true
  }
  return false
}

export function computeStepStatuses(lifecycle: OrderLifecycleResponse): SimulationStep[] {
  // Sequential steps — weighbridge_scan is parallel/optional, handled separately
  const sequentialStepIds: SimulationStepId[] = [
    'order_created',
    'assign_resource',
    'join_queue',
    'entry_scan',
    'first_weigh',
    'second_weigh',
    'exit_scan',
    'invoices',
  ]

  let foundActive = false
  const sequentialSteps = sequentialStepIds.map((id) => {
    let status: SimulationStepStatus

    if (isStepFailed(id, lifecycle)) {
      status = 'failed'
      foundActive = true
    } else if (isStepCompleted(id, lifecycle)) {
      status = 'completed'
    } else if (!foundActive) {
      status = 'active'
      foundActive = true
    } else {
      status = 'pending'
    }

    return { id, status, label: STEP_LABELS[id] }
  })

  // weighbridge_scan is parallel — active whenever entry_scan is done, never blocks first_weigh
  const entryScanDone = isStepCompleted('entry_scan', lifecycle)
  const wbDone = isStepCompleted('weighbridge_scan', lifecycle)
  const wbStatus: SimulationStepStatus = wbDone ? 'completed' : entryScanDone ? 'active' : 'pending'
  const weighbridgeStep: SimulationStep = {
    id: 'weighbridge_scan',
    status: wbStatus,
    label: STEP_LABELS['weighbridge_scan'],
  }

  // Insert weighbridge_scan immediately after entry_scan
  const entryIndex = sequentialSteps.findIndex((s) => s.id === 'entry_scan')
  const result = [...sequentialSteps]
  result.splice(entryIndex + 1, 0, weighbridgeStep)
  return result
}

function entry(message: string, status: ActivityEntry['status'], detail?: string): ActivityEntry {
  return { message, status, detail, timestamp: new Date().toISOString() }
}

export function buildActivityEntriesFromLifecycle(
  lifecycle: OrderLifecycleResponse
): Record<SimulationStepId, ActivityEntry[]> {
  const e: Record<SimulationStepId, ActivityEntry[]> = {
    order_created: [],
    assign_resource: [],
    join_queue: [],
    entry_scan: [],
    weighbridge_scan: [],
    first_weigh: [],
    second_weigh: [],
    exit_scan: [],
    invoices: [],
  }

  // ── Helper: balance summary line ──
  const cb = lifecycle.contractBalance
  const fb = lifecycle.financeBalance
  function balanceLine(): string {
    const parts: string[] = []
    if (cb?.totalNetAmount != null) parts.push(`Contract: SAR ${cb.totalNetAmount.toLocaleString()}`)
    if (fb?.amount != null) parts.push(`Finance: ${fb.currency ?? 'SAR'} ${fb.amount.toLocaleString()}`)
    return parts.length > 0 ? parts.join(' · ') : ''
  }

  // ── Order Created ──
  e.order_created.push(entry(
    `Order ${lifecycle.order.orderNumber}`,
    'success',
    `Status: ${lifecycle.order.orderStatus} · Product: ${lifecycle.order.productName ?? 'N/A'}`,
  ))
  const pricing = lifecycle.order.pricingSnapshot
  const contractAllocatedTons = pricing?.contractItems && pricing.contractItems.length > 0
    ? pricing.contractItems.reduce((sum, ci) => sum + (ci.allocatedQuantity ?? 0), 0)
    : null
  if (contractAllocatedTons != null) {
    e.order_created.push(entry(`Planned quantity: ${contractAllocatedTons}t (contract allocated)`, 'success'))
  } else if (lifecycle.order.quantity) {
    e.order_created.push(entry(`Planned quantity: ${lifecycle.order.quantity} units`, 'success'))
  }
  const bl = balanceLine()
  if (bl) {
    e.order_created.push(entry('Balances', 'success', bl))
  }

  // ── Assign Resource ──
  if (lifecycle.shipment?.hasResource) {
    e.assign_resource.push(entry('Resource assigned', 'success', lifecycle.shipment.resourceName ?? 'Assigned'))
  }

  // ── Join Queue ──
  const soJobs = lifecycle.sapSyncJobs.filter((j) => j.sapOperation === 'create_sales_order')
  for (const job of soJobs) {
    e.join_queue.push(entry(
      `SAP CPI: ${job.sapOperation}`,
      job.jobStatus === 'completed' ? 'success' : job.jobStatus === 'failed' ? 'error' : 'pending',
      buildSapJobDetail(job),
    ))
  }
  if (lifecycle.order.sapSalesOrderNumber) {
    e.join_queue.push(entry(
      `SAP Sales Order created`,
      'success',
      `SO: ${lifecycle.order.sapSalesOrderNumber}`,
    ))
    if (bl) {
      e.join_queue.push(entry('Balances after SO creation', 'success', bl))
    }
  }

  // ── Entry Scan ──
  const ps = lifecycle.order.pricingSnapshot
  if (ps) {
    e.entry_scan.push(entry(
      `SAP CPI: Material price fetched`,
      'success',
      `${ps.currency ?? 'SAR'} ${ps.unitPrice ?? '?'}/ton · Source: ${ps.source ?? 'sap_cpi'}`,
    ))
    if (ps.contractItems && ps.contractItems.length > 0) {
      for (const ci of ps.contractItems) {
        e.entry_scan.push(entry(
          `Contract item ${ci.item} allocated`,
          'success',
          `Qty: ${ci.allocatedQuantity}t · Amount: ${ps.currency ?? 'SAR'} ${ci.allocatedAmount}`,
        ))
      }
    }
  }

  if (bl) {
    e.entry_scan.push(entry('Balances after entry', 'success', bl))
  }

  // ── First Weigh ──
  const ticket = lifecycle.weighbridgeTickets[0]
  if (ticket?.tareWeight) {
    e.first_weigh.push(entry(
      `Tare weight recorded`,
      'success',
      `${ticket.tareWeight.toLocaleString()} kg · Ticket: ${ticket.ticketNumber}`,
    ))

    const fJobs = lifecycle.sapSyncJobs.filter((j) => j.sapOperation === 'weight_update_first')
    for (const job of fJobs) {
      e.first_weigh.push(entry(
        `SAP CPI: F-weight update`,
        job.jobStatus === 'completed' ? 'success' : job.jobStatus === 'failed' ? 'error' : 'pending',
        buildSapJobDetail(job) || `${(ticket.tareWeight / 1000).toFixed(2)}t → /weight/update`,
      ))
    }
    if (fJobs.length === 0) {
      e.first_weigh.push(entry(
        `SAP CPI: F-weight update sent`, 'success',
        `WeightType=F · ${(ticket.tareWeight / 1000).toFixed(2)}t → /weight/update`,
      ))
    }
    if (bl) {
      e.first_weigh.push(entry('Balances after F-weight', 'success', bl))
    }
  }

  // ── Second Weigh ──
  if (ticket?.grossWeight && ticket?.netWeight) {
    e.second_weigh.push(entry(
      `Gross weight recorded`, 'success',
      `Gross: ${ticket.grossWeight.toLocaleString()} kg · Net: ${ticket.netWeight.toLocaleString()} kg (${(ticket.netWeight / 1000).toFixed(2)}t)`,
    ))

    // S-weight update is sent at exit scan time, not here — show a note only
    e.second_weigh.push(entry(
      `SAP CPI: S-weight update`,
      'pending',
      `WeightType=S · ${(ticket.grossWeight / 1000).toFixed(2)}t — will be sent at exit scan`,
    ))

    // Financial impact
    if (ps) {
      const netTons = ticket.netWeight / 1000
      const totalAmount = (ps.unitPrice ?? 0) * netTons
      e.second_weigh.push(entry(
        `Delivery amount calculated`, 'success',
        `${netTons.toFixed(2)}t × ${ps.currency ?? 'SAR'} ${ps.unitPrice}/ton = ${ps.currency ?? 'SAR'} ${totalAmount.toFixed(2)}`,
      ))

      const allocatedTons = ps.contractItems && ps.contractItems.length > 0
        ? ps.contractItems.reduce((sum, ci) => sum + (ci.allocatedQuantity ?? 0), 0)
        : null
      const plannedLabel = allocatedTons != null ? `${allocatedTons}t (contract)` : `${lifecycle.order.quantity ?? '?'} (order)`
      const plannedTons = allocatedTons ?? lifecycle.order.quantity

      if (plannedTons) {
        const variance = netTons - plannedTons
        e.second_weigh.push(entry(
          `Quantity variance`,
          variance > 0.01 ? 'error' : 'success',
          `Actual: ${netTons.toFixed(2)}t vs Planned: ${plannedLabel} · Variance: ${variance > 0 ? '+' : ''}${variance.toFixed(2)}t${variance > 0.01 ? ' (overload)' : ''}`,
        ))
      }

      if (ps.contractItems && ps.contractItems.length > 0) {
        const allocatedAmount = ps.contractItems.reduce((sum, ci) => sum + (ci.allocatedAmount ?? 0), 0)
        e.second_weigh.push(entry(
          `Contract balance impact`,
          totalAmount > allocatedAmount ? 'error' : 'success',
          `Allocated: ${ps.currency ?? 'SAR'} ${allocatedAmount.toFixed(2)} · Actual: ${ps.currency ?? 'SAR'} ${totalAmount.toFixed(2)} · ${totalAmount > allocatedAmount ? 'Over by ' + (totalAmount - allocatedAmount).toFixed(2) : 'Within budget'}`,
        ))
      }
    }

    // Delivery line — show raw value without assuming unit
    if (lifecycle.deliveryLines.length > 0) {
      const dl = lifecycle.deliveryLines[0]
      e.second_weigh.push(entry(
        `Delivery line synced`, 'success',
        `Delivered qty: ${dl.deliveredQuantity ?? '?'} · Status: ${dl.lineStatus ?? 'N/A'}`,
      ))
    }

    if (bl) {
      e.second_weigh.push(entry('Balances after S-weight', 'success', bl))
    }

    // Invoices generated after delivery
    if (lifecycle.invoices.length > 0) {
      for (const inv of lifecycle.invoices) {
        e.second_weigh.push(entry(
          `Invoice: ${inv.sapInvoiceNumber ?? inv.invoiceNumber ?? 'N/A'}`, 'success',
          `${inv.currency ?? 'SAR'} ${inv.totalAmount ?? inv.sapAmount ?? '?'}${inv.subtotal ? ` (subtotal: ${inv.subtotal}, VAT: ${inv.vatAmount ?? 0})` : ''} · ${inv.invoiceStatus ?? ''}`,
        ))
      }
    }
  }

  // ── Exit Scan ──
  if (lifecycle.shipment) {
    const s = lifecycle.shipment

    // weight_update_second SAP jobs — created at exit scan time
    const sExitJobs = lifecycle.sapSyncJobs.filter((j) => j.sapOperation === 'weight_update_second')
    for (const job of sExitJobs) {
      const isError = job.jobStatus === 'dlq' || job.jobStatus === 'failed'
      const isSuccess = job.jobStatus === 'completed'
      e.exit_scan.push(entry(
        `SAP CPI: Second weight update (WeightType=S)`,
        isError ? 'error' : isSuccess ? 'success' : 'pending',
        buildSapJobDetail(job),
      ))
      if (isError) {
        // Surface the SAP error message from the response payload (e.g. OVERLOAD BLOCKED)
        const payload = job.responsePayload as any
        const sapMsg = payload?.message ?? payload?.data?.message ?? payload?.data?.data?.message
        if (sapMsg) {
          e.exit_scan.push(entry(`SAP error`, 'error', String(sapMsg)))
        }
        e.exit_scan.push(entry(
          `Exit blocked — shipment NOT delivered`,
          'error',
          `Shipment remains in: ${s.shipmentStatus}`,
        ))
      }
    }

    if (s.shipmentStatus === 'completed' || s.shipmentStatus === 'delivered') {
      e.exit_scan.push(entry(`Shipment delivered`, 'success', `Shipment: ${s.shipmentNumber}`))
    }
    if (s.holdReason) {
      e.exit_scan.push(entry(`Shipment on hold`, 'error', s.holdReason))
    }
    if (bl) {
      e.exit_scan.push(entry('Balances after exit', 'success', bl))
    }
  }

  // ── Invoices ──
  if (lifecycle.invoices.length > 0) {
    for (const inv of lifecycle.invoices) {
      e.invoices.push(entry(
        `${inv.sapInvoiceNumber ?? inv.invoiceNumber ?? 'N/A'}`,
        'success',
        `${inv.currency ?? 'SAR'} ${inv.totalAmount ?? inv.sapAmount ?? '?'}${inv.subtotal ? ` (subtotal: ${inv.subtotal}, VAT: ${inv.vatAmount ?? 0})` : ''} · ${inv.invoiceStatus ?? ''}`,
      ))
    }
  }

  return e
}

function buildSapJobDetail(job: any): string {
  const parts: string[] = []
  if (job.httpMethod && job.url) {
    parts.push(`${job.httpMethod} ${job.url}`)
  }
  if (job.responseStatus) {
    parts.push(`HTTP ${job.responseStatus}`)
  }
  if (job.durationMs) {
    parts.push(`${job.durationMs}ms`)
  }
  if (job.sapDocumentNumber) {
    parts.push(`Doc: ${job.sapDocumentNumber}`)
  }
  if (job.errorMessage) {
    parts.push(job.errorMessage)
  }
  return parts.join(' · ') || ''
}
